import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {Card, Flex, message, Radio, Select, Table} from "antd";
import {FilePdfOutlined, FileWordOutlined} from "@ant-design/icons";
import IntlMessage from "../../../../components/IntlMessage/IntlMessage";
import GreyButton from "../../../../components/UI/Buttons/GreyButton";
import {clrs} from "../../../../constants/colors";
import ActCompletionWorkService from "../../../../services/ActCompletionWorkService";
import UserService from "../../../../services/UserService";
import classes from "./actCompletionWorkPage.module.css";
import {LocalName} from "../../../../utils/LocalName";
import {useDispatch, useSelector} from "react-redux";
import {getActCompletionWorks} from "../../../../store/slices/ActCompletionWorkSlice";
import {
    changeCurrentPage,
    changeSelectedCategoryId,
    changeSelectedTeacherId
} from "../../../../store/slices/tableController/ActCompletionWorkTableControllerSlice";

const ActCompletionWorkPage = () => {
    const dispatch = useDispatch();
    const categoriesList = useSelector(state => state.categoriesList);
    const actCompletionWorkList = useSelector(state => state.actCompletionWorkList);
    const tableController = useSelector(state => state.actCompletionWorkTableController);

    const [teachers, setTeachers] = useState([]);
    const [loadingTeachers, setLoadingTeachers] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        fetchTeachers();
    }, []);

    useEffect(() => {
        handleFetchData();
    }, [tableController.currentPage, tableController.selectedCategoryId, tableController.selectedTeacherId]);

    async function fetchTeachers() {
        setLoadingTeachers(true);
        try {
            const response = await UserService.getTeachers(1, 1000);
            setTeachers(response.data);
        } catch (error) {
            message.error(<IntlMessage id={'error'}/>, 5);
        } finally {
            setLoadingTeachers(false);
        }
    }

    function handleFetchData() {
        dispatch(getActCompletionWorks({
            page: tableController.currentPage,
            limit: tableController.pageSize,
            categoryId: tableController.selectedCategoryId,
            teacherId: tableController.selectedTeacherId
        }));
    }

    const handlePaginationChange = (page, pageSize) => {
        dispatch(changeCurrentPage({page, pageSize}));
    }

    function triggerDownload(blob, filename) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    // format: 'pdf' | 'docx' — Word отдаём, чтобы сотрудники могли править документ
    async function downloadDocumentACW(documentId, format = 'pdf') {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await ActCompletionWorkService.getACWResource(documentId, format)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);

                triggerDownload(r.data, "act_completion_work." + format)
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    }

    async function downloadDocumentASP(documentId, format = 'pdf') {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await ActCompletionWorkService.getASPResource(documentId, format)
            .then((r) => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5);

                // своё имя, чтобы АСП не путался с АВР в загрузках
                triggerDownload(r.data, "asp." + format)
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    }

    const columns = [
        {
            title: <span style={{fontWeight: 600}}><IntlMessage id={'group'}/></span>,
            render: (_, record) => (
                <p>{LocalName.getName(record.group) + " " + record.group.number}</p>
            ),
            width: '20%'
        },
        {
            title: <span style={{fontWeight: 600}}><IntlMessage id={'acw.dateOfCompilation'}/></span>,
            render: (_, record) => (
                <p>{new Date(record.compilationDate).toLocaleDateString("ru-RU")}</p>
            ),
            width: '10%'
        },
        {
            title: <span style={{fontWeight: 600}}><IntlMessage id={'ordersPage.teacher'}/></span>,
            render: (_, record) => (
                <p>{record.teacherFullName}</p>
            ),
            width: '20%'
        },
        {
            title: <span style={{fontWeight: 600}}><IntlMessage id={'number'}/></span>,
            render: (_, record) => (
                <p>{record.number}</p>
            ),
            width: '10%'
        },
        {
            title: <IntlMessage id={'actions'}/>,
            render: (_, record) =>
                <>
                    <Flex style={{width: "100%"}} align={"flex-start"} justify={"center"} vertical gap={6}>
                        <Flex align={"center"} wrap={"wrap"} gap={10}>
                            <span className={classes.docLabel}><IntlMessage id={'acw.acw'}/></span>
                            <p onClick={() => downloadDocumentACW(record.id, 'pdf')} className={classes.copyLinkBtn}><FilePdfOutlined/> PDF</p>
                            <p onClick={() => downloadDocumentACW(record.id, 'docx')} className={classes.copyLinkBtn}><FileWordOutlined/> Word</p>
                        </Flex>
                        <Flex align={"center"} wrap={"wrap"} gap={10}>
                            <span className={classes.docLabel}><IntlMessage id={'acw.aas'}/></span>
                            <p onClick={() => downloadDocumentASP(record.id, 'pdf')} className={classes.copyLinkBtn}><FilePdfOutlined/> PDF</p>
                            <p onClick={() => downloadDocumentASP(record.id, 'docx')} className={classes.copyLinkBtn}><FileWordOutlined/> Word</p>
                        </Flex>
                    </Flex>

                </>,
            width: '15%',
        },
    ];

    return (
        <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} vertical gap={20}>
            <Card style={{width: "100%", borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
                <Flex style={{width: "100%"}} vertical align={"flex-start"} justify={"flex-start"} gap={16}>
                    <Radio.Group
                        value={tableController.selectedCategoryId}
                        onChange={(e) => {
                            dispatch(changeSelectedCategoryId(e.target.value));
                        }}
                    >
                        <Radio value={null}>
                            <IntlMessage id={'allCategories'}/>
                        </Radio>
                        {
                            categoriesList?.categories?.map((category) => (
                                <Radio value={category.id} key={category.id}>
                                    {LocalName.getName(category)}
                                </Radio>
                            ))
                        }
                    </Radio.Group>

                    <Select
                        style={{width: '100%', maxWidth: 400}}
                        placeholder={<IntlMessage id={'ordersPage.teacher'}/>}
                        allowClear
                        showSearch
                        loading={loadingTeachers}
                        value={tableController.selectedTeacherId}
                        onChange={(value) => {
                            dispatch(changeSelectedTeacherId(value));
                        }}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={[
                            ...teachers.map(teacher => ({
                                value: teacher.id,
                                label: teacher.fullName
                            }))
                        ]}
                    />
                </Flex>
            </Card>
            <GreyButton onClick={() => {
                handleFetchData();
            }} backgroundColor={clrs.whiter} fontColor={clrs.black}><IntlMessage id={'update'}/></GreyButton>

            <Table
                bordered
                columns={columns}
                dataSource={actCompletionWorkList.actCompletionWorks}
                pagination={{
                    current: tableController.currentPage,
                    total: actCompletionWorkList.hasMore
                        ? tableController.currentPage * tableController.pageSize + 1
                        : tableController.currentPage * tableController.pageSize,
                    defaultPageSize: tableController.pageSize,
                    showSizeChanger: true,
                    pageSize: tableController.pageSize,
                    pageSizeOptions: ['5', '10', '20', '30', '50', '100'],
                    onChange: handlePaginationChange,
                }}
                rowKey={record => record.id}
                loading={actCompletionWorkList.isLoading}
                style={{
                    width: "100%",
                }}
                scroll={{x: 800}}
            />
        </Flex>
    );
};

export default ActCompletionWorkPage;
